import type { BoxProps } from '@hover/blueprint';
import { Box } from '@hover/blueprint';
import {
  CompositorCanvas,
  useCompositorControls,
} from '@hoverinc/rendering-compositor-react';
import React, { useContext, useEffect } from 'react';
import type { StateValue } from 'xstate';

import { HelpOverlay } from 'src/modules/designStudio/components/DesignStudio/InteractiveRendering/HelpOverlay';
import {
  LANDSCAPE_TOOLBAR_WIDTH,
  TOOLBAR_HEIGHT,
} from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import { Menu } from 'src/modules/designStudio/constants';
import { InteractiveRenderingStateContext } from 'src/modules/designStudio/providers';
import { useIsLandscape } from 'src/shared/hooks';

interface InteractiveRendering {
  appState?: StateValue;
  menu: Menu | null;
  setMenu: (menu: Menu | null) => void;
  finishLoadingRendering: () => void;
}

const InteractiveRenderingCanvas = ({
  appState,
  finishLoadingRendering,
  menu,
  setMenu,
}: InteractiveRendering) => {
  const { isLoading } = useCompositorControls();
  const { clickedRegion, renderingFile, showHelpOverlay, setShowHelpOverlay } =
    useContext(InteractiveRenderingStateContext);
  const [isLandscape] = useIsLandscape();
  const containerStyles: BoxProps = {
    overflow: 'none',
    position: 'absolute',
    top: 0,
  };

  if (isLandscape) {
    containerStyles.width = `calc(100% - ${LANDSCAPE_TOOLBAR_WIDTH}px)`;
    containerStyles.height = '100vh';
    containerStyles.left = `${LANDSCAPE_TOOLBAR_WIDTH}px`;
  } else {
    containerStyles.width = '100vw';
    containerStyles.height = menu ? '50vh' : `calc(100% - ${TOOLBAR_HEIGHT}px)`;
  }
  containerStyles.transition = menu ? 'width 600ms ease-out' : '';

  useEffect(() => {
    if (appState === 'loading' && !isLoading) {
      finishLoadingRendering();
    }
  }, [appState, finishLoadingRendering, isLoading]);

  // We use clickedRegion instead of selectedRegion as this effect only changes menus in response to canvas click
  // events, not all changes to selectedRegion. For example, we do not want to run this effect if deselectAllRegions is
  // executed. We intentionally run the effect on all clickedRegion updates, regardless if the regionId of
  // clickedRegion has actually changed.
  useEffect(() => {
    const regions = renderingFile?.cryptomatteRegions?.regions ?? [];

    if (clickedRegion.regionId) {
      const selectedRegionCategory = regions.find(
        region => region.regionId === clickedRegion.regionId,
      )?.category;
      switch (selectedRegionCategory) {
        case 'WALLS':
          setMenu(Menu.INTERACTIVE_RENDERING_WALL);
          break;
        case 'ROOFS':
          setMenu(Menu.INTERACTIVE_RENDERING_ROOF);
          break;
        case 'TRIM':
          setMenu(Menu.INTERACTIVE_RENDERING_TRIM);
          break;
        default:
          setMenu(null);
      }
    } else {
      setMenu(null);
    }
  }, [clickedRegion, setMenu, renderingFile]);

  // NOTE: we're using `sizeMode` set to `contain` on the `CompositorProvider`,
  // so the `CompositorCanvas` element below must have explicit height and width
  // dimensions. It defaults to a CSS width and height of `100%`, so the
  // `containerStyles` currently provide said explicit dimensions.
  return (
    <Box testId="interactive-rendering-container" {...containerStyles}>
      <CompositorCanvas />
      {showHelpOverlay && (
        <HelpOverlay onAnimationDone={() => setShowHelpOverlay(false)} />
      )}
    </Box>
  );
};

export { InteractiveRenderingCanvas };
